const blurValues = {
  sm: "blur(6px)",
  md: "blur(14px)",
  lg: "blur(20px)",
  xl: "blur(28px)",
};

const shadowStyles = {
  none: "none",
  xs: "inset 1px 1px 1px 0 rgba(255,255,255,0.35), inset -1px -1px 1px 0 rgba(255,255,255,0.2)",
  sm: "inset 2px 2px 2px 0 rgba(255,255,255,0.4), inset -2px -2px 2px 0 rgba(255,255,255,0.2)",
  md: "inset 3px 3px 3px 0 rgba(255,255,255,0.5), inset -3px -3px 3px 0 rgba(255,255,255,0.25)",
  lg: "inset 4px 4px 4px 0 rgba(255,255,255,0.55), inset -4px -4px 4px 0 rgba(255,255,255,0.3)",
  xl: "inset 6px 6px 6px 0 rgba(255,255,255,0.6), inset -6px -6px 6px 0 rgba(255,255,255,0.35)",
};

const glowStyles = {
  none: "none",
  xs: "0 4px 16px rgba(0,0,0,0.12)",
  sm: "0 8px 32px rgba(0,0,0,0.15), 0 2px 8px rgba(0,0,0,0.08)",
  md: "0 12px 40px rgba(0,0,0,0.18), 0 4px 12px rgba(0,0,0,0.10)",
  lg: "0 16px 56px rgba(0,0,0,0.22), 0 6px 16px rgba(0,0,0,0.12)",
  xl: "0 24px 64px rgba(0,0,0,0.28), 0 8px 20px rgba(0,0,0,0.14)",
};

/**
 * Liquid glass morphism card — adapted from ui-layouts.com/components/liquid-glass.
 * Works without Tailwind or framer-motion.
 *
 * The `background` tint lives on the backdrop-filter layer so the blur composites
 * correctly over whatever is behind the card.
 *
 * Props:
 *   background     – CSS color string for the glass tint (default: rgba(255,255,255,0.12))
 *   blurIntensity  – 'sm' | 'md' | 'lg' | 'xl'
 *   shadowIntensity – 'none'|'xs'|'sm'|'md'|'lg'|'xl'
 *   glowIntensity  – 'none'|'xs'|'sm'|'md'|'lg'|'xl'
 *   borderRadius   – CSS string
 *   filterId       – unique SVG filter id (needed when >1 card on page)
 *   style          – extra styles on the outer wrapper (no background here)
 */
export default function LiquidGlassCard({
  children,
  style = {},
  background = "rgba(255,255,255,0.12)",
  blurIntensity = "xl",
  shadowIntensity = "md",
  glowIntensity = "md",
  borderRadius = "20px",
  filterId = "liquid-glass-filter",
  ...props
}) {
  const blur = blurValues[blurIntensity] ?? blurValues.xl;
  const shadow = shadowStyles[shadowIntensity] ?? shadowStyles.md;
  const glow = glowStyles[glowIntensity] ?? glowStyles.md;

  return (
    <>
      {/* SVG turbulence — creates the subtle liquid warping on the glass edge */}
      <svg style={{ display: "none" }} aria-hidden>
        <defs>
          <filter
            id={filterId}
            x="-5%"
            y="-5%"
            width="110%"
            height="110%"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.004 0.008"
              numOctaves="1"
              result="turbulence"
            />
            <feDisplacementMap
              in="SourceGraphic"
              in2="turbulence"
              scale="8"
              xChannelSelector="R"
              yChannelSelector="G"
            />
          </filter>
        </defs>
      </svg>

      {/* Outer wrapper — no background; size/position only */}
      <div
        style={{
          position: "relative",
          borderRadius,
          overflow: "hidden",
          ...style,
        }}
        {...props}
      >
        {/* ① Backdrop-blur + tint layer
              backdrop-filter blurs the raw scene behind the card.
              background sits on the SAME element so it composites
              over the already-blurred result → frosted glass. */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            borderRadius,
            background,
            backdropFilter: blur,
            WebkitBackdropFilter: blur,
            zIndex: 0,
          }}
        />

        {/* ② Glass border — thin white top-left highlight */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            borderRadius,
            border: "1px solid rgba(255,255,255,0.35)",
            zIndex: 1,
          }}
        />

        {/* ③ Inset rim highlights */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            borderRadius,
            boxShadow: shadow,
            zIndex: 2,
          }}
        />

        {/* ④ Outer depth / glow */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            borderRadius,
            boxShadow: glow,
            zIndex: 3,
          }}
        />

        {/* ⑤ Content sits above all glass layers */}
        <div style={{ position: "relative", zIndex: 4 }}>
          {children}
        </div>
      </div>
    </>
  );
}
